import {
  LAYOUT_ANIMATION_DURATION,
  LAYOUT_ANIMATION_EASING,
  layoutPositionedNodeSelector,
} from './constants';
import { queryBoardNode } from '../../cardBitmaps/findTaskElement';

const POSITION_DELTA_PX = 2;
const MAX_WATCH_MS = 2500;
/** Only used if ELK never announces a destination. */
export const CONNECT_DESTINATION_SETTLE_MS = 90;

let panClearTimeoutId = null;

function parsePx(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function positionsDiffer(a, b, eps = POSITION_DELTA_PX) {
  if (!a || !b) return a !== b;
  return Math.abs(a.x - b.x) > eps || Math.abs(a.y - b.y) > eps;
}

function readLiveCanvasPanZoom(canvas) {
  const transform = canvas?.style?.transform || '';
  const translateMatch = transform.match(/translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/);
  const scaleMatch = transform.match(/scale\(\s*([\d.]+)\s*\)/);
  return {
    pan: translateMatch
      ? { x: parseFloat(translateMatch[1]), y: parseFloat(translateMatch[2]) }
      : { x: 0, y: 0 },
    zoom: scaleMatch ? parseFloat(scaleMatch[1]) : 1,
  };
}

function applyCanvasPan(canvas, pan, zoom, { withTransitionMs = 0 } = {}) {
  if (withTransitionMs > 0) {
    canvas.style.transition = `transform ${withTransitionMs}ms ${LAYOUT_ANIMATION_EASING}`;
  } else {
    canvas.style.transition = '';
  }
  canvas.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
  canvas.style.setProperty('--inverse-zoom', String(1 / zoom));
}

/**
 * Read a mission's canvas coordinates from the live node, preferring the
 * inline FLIP target (`transform`) so we get the destination, not a
 * mid-transition computed matrix.
 */
export function readLayoutNodePosition(taskId) {
  if (!taskId || typeof document === 'undefined') return null;
  const el = queryBoardNode(layoutPositionedNodeSelector(taskId));
  if (!el) return null;

  const inline = el.style.transform || '';
  const inlineMatch = inline.match(/translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/);
  if (inlineMatch) {
    return { x: parseFloat(inlineMatch[1]), y: parseFloat(inlineMatch[2]) };
  }

  const tx = parsePx(el.style.getPropertyValue('--node-tx'));
  const ty = parsePx(el.style.getPropertyValue('--node-ty'));
  if (tx != null && ty != null) return { x: tx, y: ty };

  return null;
}

function readLayoutNodeSize(taskId) {
  const el = queryBoardNode(layoutPositionedNodeSelector(taskId));
  if (!el) return { width: 290, height: 150 };
  return {
    width: el.offsetWidth || 290,
    height: el.offsetHeight || 150,
  };
}

/**
 * One CSS slide to center `position`. No per-frame JS and no `panupdate`
 * until the slide finishes — those reculls are what hitch large campaigns.
 */
export function animateCanvasToCenterPosition(position, {
  width = 290,
  height = 150,
  duration = LAYOUT_ANIMATION_DURATION,
  setPan = null,
} = {}) {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return false;
  const container = typeof document === 'undefined' ? null : document.querySelector('.flowchart');
  const canvas = container?.querySelector('.flowchart__canvas');
  if (!container || !canvas) return false;

  const rect = container.getBoundingClientRect();
  const { pan: startPan, zoom } = readLiveCanvasPanZoom(canvas);
  const targetPan = {
    x: rect.width / 2 - (position.x + width / 2) * zoom,
    y: rect.height / 2 - (position.y + height / 2) * zoom,
  };

  const dx = targetPan.x - startPan.x;
  const dy = targetPan.y - startPan.y;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    applyCanvasPan(canvas, targetPan, zoom);
    setPan?.(targetPan);
    return true;
  }

  if (panClearTimeoutId != null) {
    clearTimeout(panClearTimeoutId);
    panClearTimeoutId = null;
  }

  const dur = Math.max(duration || LAYOUT_ANIMATION_DURATION, 1);
  // Force current transform so the upcoming transition interpolates from here.
  applyCanvasPan(canvas, startPan, zoom);
  void canvas.offsetWidth;
  applyCanvasPan(canvas, targetPan, zoom, { withTransitionMs: dur });

  panClearTimeoutId = setTimeout(() => {
    panClearTimeoutId = null;
    applyCanvasPan(canvas, targetPan, zoom);
    canvas.dispatchEvent(new CustomEvent('panupdate', {
      detail: { x: targetPan.x, y: targetPan.y, zoom },
    }));
    setPan?.(targetPan);
  }, dur + 40);

  return true;
}

export function centerCanvasOnConnectTarget(taskId, position, durationMs, setPan) {
  const pos = position || readLayoutNodePosition(taskId);
  if (!pos) return false;
  const size = readLayoutNodeSize(taskId);
  return animateCanvasToCenterPosition(pos, {
    ...size,
    duration: durationMs,
    setPan,
  });
}

/**
 * Fallback: wait until the drop-target has a stable destination, then pan once.
 *
 * @returns {Function} cancel
 */
export function startWatchingConnectTarget({
  taskId,
  onCenter,
  settleMs = CONNECT_DESTINATION_SETTLE_MS,
}) {
  let rafId = null;
  let stopped = false;
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const initial = readLayoutNodePosition(taskId);
  let lastPos = initial;
  let lastChangeAt = startedAt;
  let hasMoved = false;

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  const finish = (pos) => {
    if (stopped) return;
    stopped = true;
    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = null;
    if (pos) onCenter?.(pos, LAYOUT_ANIMATION_DURATION);
  };

  const tick = () => {
    if (stopped) return;
    const pos = readLayoutNodePosition(taskId);
    const t = now();

    if (pos && positionsDiffer(pos, lastPos)) {
      lastPos = pos;
      lastChangeAt = t;
      hasMoved = true;
    }

    if (hasMoved && t - lastChangeAt >= settleMs) {
      finish(lastPos);
      return;
    }

    if (!hasMoved && t - startedAt >= MAX_WATCH_MS) {
      finish(pos || initial);
      return;
    }

    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    stopped = true;
    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = null;
  };
}
